import { useState } from 'react';
import { toast } from 'react-toastify';
import { FiMail, FiDownload } from 'react-icons/fi';
import Modal from './Modal';
import { emailExcelReport } from '../../services/api';

/**
 * ExcelExportModal
 *
 * Props:
 *   onClose        — () => void
 *   onDownload     — async () => void  — triggers the existing Excel download
 *   exportPayload  — object            — full export data sent to POST /reports/email-excel
 *   defaultEmailTo — string            — pre-filled recipient email (may be empty)
 */
export default function ExcelExportModal({ onClose, onDownload, exportPayload, defaultEmailTo }) {
  const [emailAddress, setEmailAddress] = useState(defaultEmailTo || '');
  const [emailSending, setEmailSending]  = useState(false);
  const [downloading, setDownloading]    = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onDownload();
      onClose();
    } finally {
      setDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailAddress.trim()) {
      toast.error('Please enter a recipient email address');
      return;
    }
    setEmailSending(true);
    try {
      await emailExcelReport({ ...exportPayload, to: emailAddress.trim() });
      toast.success('Excel report sent by email');
      onClose();
    } catch (err) {
      console.error('Excel email failed', err);
      toast.error(err?.response?.data?.message || 'Failed to send Excel report');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Modal
      title="Export as Excel"
      onClose={onClose}
      size="sm"
      actions={
        <>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleSendEmail}
            disabled={emailSending || !emailAddress.trim()}
          >
            <FiMail size={14} /> {emailSending ? 'Sending…' : 'Send Email'}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleDownload}
            disabled={downloading}
          >
            <FiDownload size={14} /> {downloading ? 'Downloading…' : 'Download'}
          </button>
        </>
      }
    >
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Recipient Email</label>
        <input
          type="email"
          placeholder="recipient@example.com"
          value={emailAddress}
          onChange={(e) => setEmailAddress(e.target.value)}
          autoFocus
        />
      </div>
    </Modal>
  );
}
